import type { Identifier } from "ra-core";
import { getSupabaseClient } from "../providers/supabase/supabase";

/** Off unless the build was made with the flag. The function has its own check. */
export const aiNoteExtractEnabled =
  import.meta.env.VITE_ENABLE_AI_NOTE_EXTRACT === "true" &&
  import.meta.env.VITE_IS_DEMO !== "true";

export type FieldChange = {
  field: "status" | "title";
  from: string | null;
  to: string;
  quote?: string;
};

export type NextActionProposal = {
  title: string;
  due_date: string | null;
  due_time?: string | null;
};

export type ExistingNextAction = {
  id: number;
  title: string;
  due_date: string | null;
};

export type NoteProposal = {
  proposal_id: string;
  summary: string;
  contact_id: number;
  contact_type: string;
  changes: FieldChange[];
  next_action: NextActionProposal | null;
  existing_next_action: ExistingNextAction | null;
};

export type NextActionChoice = "replace" | "complete_and_create";

export type ApplyResult = {
  decision: "approved" | "rejected" | "partially_applied";
  applied_diff?: unknown;
};

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await getSupabaseClient().functions.invoke<T>(
    "ai-note-extract",
    { body },
  );
  if (error) {
    let code = "request_failed";
    try {
      const payload = await error.context?.json?.();
      if (typeof payload?.error === "string") code = payload.error;
    } catch {
      code = error.message || code;
    }
    throw new Error(code);
  }
  if (!data) throw new Error("empty");
  return data;
}

export function requestNoteProposal(contactId: Identifier, text: string) {
  return invoke<{ proposal: NoteProposal }>({
    mode: "propose",
    contact_id: Number(contactId),
    text,
  });
}

export function applyNoteProposal(input: {
  contactId: Identifier;
  proposalId: string;
  fields: { status: boolean; title: boolean };
  nextAction: boolean;
  nextActionChoice?: NextActionChoice;
}) {
  return invoke<ApplyResult>({
    mode: "apply",
    contact_id: Number(input.contactId),
    proposal_id: input.proposalId,
    fields: input.fields,
    next_action: input.nextAction,
    next_action_choice: input.nextActionChoice,
  });
}

export function rejectNoteProposal(contactId: Identifier, proposalId: string) {
  return invoke<{ decision: "rejected" }>({
    mode: "reject",
    contact_id: Number(contactId),
    proposal_id: proposalId,
  });
}

export function proposalErrorText(code: string): string {
  switch (code) {
    case "choice_required":
      return "palmtree.notes.error_choice";
    case "invalid_model_output":
      return "palmtree.notes.error_invalid";
    case "AI provider error":
      return "palmtree.notes.error_provider";
    case "disabled":
      return "palmtree.notes.error_disabled";
    case "empty_selection":
      return "palmtree.notes.error_empty";
    case "open_next_action_exists":
      return "palmtree.notes.error_open";
    case "status_not_allowed":
      return "palmtree.notes.error_status";
    default:
      return "palmtree.notes.error_generic";
  }
}
